if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        this.initInlineVideoAutoplay();

        if (!this.elements.thumbnails || !this.elements.viewer) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 300)
        );

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;

          button.addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          );
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }

        this.initActiveDotSync();
      }

      disconnectedCallback() {
        if (this.inlineVideoObserver) this.inlineVideoObserver.disconnect();
        if (this.dotObserver) this.dotObserver.disconnect();
      }

      // The slider's scroll math can mis-track non-image slides, leaving the mobile dots
      // (thumbnail bullets) stuck when scrolling onto a video. Sync the active dot to the
      // slide that is actually most visible in the viewer while scrolling.
      initActiveDotSync() {
        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        const slides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
        if (!slider || slides.length < 2 || !('IntersectionObserver' in window)) return;

        const ratios = new Map();
        this.dotObserver = new IntersectionObserver(
          (entries) => {
            if (this.mql.matches) return;

            entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio));

            let mostVisible = null;
            let highestRatio = 0;
            slides.forEach((slide) => {
              const ratio = ratios.get(slide) || 0;
              if (ratio > highestRatio) {
                highestRatio = ratio;
                mostVisible = slide;
              }
            });

            if (!mostVisible || highestRatio < 0.5) return;

            const thumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mostVisible.dataset.mediaId}"]`
            );
            this.setActiveThumbnail(thumbnail);
          },
          { root: slider, threshold: [0, 0.25, 0.5, 0.75, 1] }
        );

        slides.forEach((slide) => this.dotObserver.observe(slide));
      }

      // HOVER :
      // antive video are rendered as plein inline video/poster elements (check product-thumbnail)
      // play only the one scrolled into view and pause the rest, so a carousel with multiple
      // videos stays smooth and there's no poster->video swap/flash 
      initInlineVideoAutoplay() {
        const videos = this.querySelectorAll('video.product-media-container__video');
        if (!videos.length) return;

        if (!('IntersectionObserver' in window)) {
          videos.forEach((video) => this.tryPlayInline(video));
          return;
        }

        this.inlineVideoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                this.tryPlayInline(entry.target);
              } else {
                entry.target.pause();
              }
            });
          },
          { threshold: [0, 0.6, 1] }
        );

        videos.forEach((video) => this.inlineVideoObserver.observe(video));
      }

      tryPlayInline(video) {
        if (!video) return;
        // forcing this for safari !
        video.muted = true;
        video.playsInline = true;
        const played = video.play();
        if (played && played.catch) played.catch(() => {});
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        if (!mediaId) return;

        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
        this.setActiveMedia(mediaId, false);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.pauseAllMediaInGallery();

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );

            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          if (prepend) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const inlineVideo = activeItem.querySelector('video.product-media-container__video');
        if (inlineVideo) {
          this.tryPlayInline(inlineVideo);
          return;
        }

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        const posterButton = deferredMedia.querySelector('.deferred-media__poster');
        const existingVideo = deferredMedia.querySelector('video');
        const existingIframe = deferredMedia.querySelector('iframe');

        // Already loaded before
        if (existingVideo) {
          this.prepareAndPlayVideo(existingVideo, posterButton);
          return;
        }

        if (existingIframe) {
          this.playIframe(existingIframe, posterButton);
          return;
        }

        // If not loaded yet, load now
        deferredMedia.loadContent(false);

        let attempts = 0;
        const interval = setInterval(() => {
          attempts += 1;

          const video = deferredMedia.querySelector('video');
          const iframe = deferredMedia.querySelector('iframe');

          if (video) {
            clearInterval(interval);
            this.prepareAndPlayVideo(video, posterButton);
            return;
          }

          if (iframe) {
            clearInterval(interval);
            this.playIframe(iframe, posterButton);
            return;
          }

          if (attempts > 30) {
            clearInterval(interval);
          }
        }, 50);
      }

      prepareAndPlayVideo(video, posterButton) {
        if (!video) return;

        video.muted = true;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.setAttribute('muted', '');
        video.setAttribute('autoplay', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'auto');

        let posterHidden = false;

        const hidePoster = () => {
          if (posterHidden) return;
          posterHidden = true;

          if (posterButton) {
            posterButton.classList.add('media-poster--hidden');
          }
        };

        const hidePosterWhenFrameIsReady = () => {
          // Best option: wait for first painted frame
          if (typeof video.requestVideoFrameCallback === 'function') {
            video.requestVideoFrameCallback(() => {
              requestAnimationFrame(() => {
                hidePoster();
              });
            });
            return;
          }

          // Fallback for browsers without requestVideoFrameCallback
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              hidePoster();
            });
          });
        };

        video.addEventListener(
          'playing',
          () => {
            hidePosterWhenFrameIsReady();
          },
          { once: true }
        );

        // Extra fallback
        video.addEventListener(
          'loadeddata',
          () => {
            if (!posterHidden && video.readyState >= 2) {
              hidePosterWhenFrameIsReady();
            }
          },
          { once: true }
        );

        if (video.readyState < 3) {
          try {
            video.load();
          } catch (e) {}
        }

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }

        // Last fallback
        setTimeout(() => {
          if (!posterHidden && video.readyState >= 3 && !video.paused) {
            hidePosterWhenFrameIsReady();
          }
        }, 300);
      }

      playIframe(iframe, posterButton) {
        if (!iframe) return;

        if (posterButton) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              posterButton.classList.add('media-poster--hidden');
            });
          });
        }

        const src = iframe.getAttribute('src') || '';

        try {
          if (src.includes('youtube.com') || src.includes('youtu.be')) {
            iframe.contentWindow?.postMessage(
              '{"event":"command","func":"playVideo","args":""}',
              '*'
            );
          } else if (src.includes('vimeo.com')) {
            iframe.contentWindow?.postMessage('{"method":"play"}', '*');
          }
        } catch (e) {}
      }

      pauseAllMediaInGallery() {
        this.elements.viewer.querySelectorAll('video').forEach((video) => {
          try {
            video.pause();
          } catch (e) {}
        });

        this.elements.viewer.querySelectorAll('iframe').forEach((iframe) => {
          const src = iframe.getAttribute('src') || '';

          try {
            if (src.includes('youtube.com') || src.includes('youtu.be')) {
              iframe.contentWindow?.postMessage(
                '{"event":"command","func":"pauseVideo","args":""}',
                '*'
              );
            } else if (src.includes('vimeo.com')) {
              iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
            }
          } catch (e) {}
        });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  );
}